import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { CertificateDetailsForm } from '@features/certificate/models/certificate-details/certificate-details.model'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { AlbertInputWrapper } from '@features/certificate/components/certificate-input-wrapper/albert-input-wrapper'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertDatepickerComponent } from '@shared/components/albert-datepicker/albert-datepicker.component'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { CertificateFreetextSectionComponent } from '@features/certificate/components/certificate-freetext-section/certificate-freetext-section.component'
import { FreetextSection } from '@features/certificate/models/certificate-shared.model'
import { freetextSection, setupFreetextSections } from '@features/certificate/utils/freetext-utils'
import { InputText } from 'primeng/inputtext'
import { IftaLabel } from 'primeng/iftalabel'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { startWith } from 'rxjs'
import { hasActualValue, syncFormCategoryState } from '@core/utils/form-utils'
import {
  AUTHORITIES_SPECIAL_FORMAT_OPTIONS,
  CERTIFICATE_AUTHOR_OPTIONS,
  CERTIFICATE_STATUS,
  DOCUMENT_STATUS,
  SUBJECT_TO_CERTIFICATION_OPTIONS
} from '@features/certificate/models/certificate-select-options.model'
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete'
import { TableModule } from 'primeng/table'
import { CertificateMetaDataForm, MetaDataRow } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { ParentCompany } from '@features/insured/models/member/member.model'
import { MemberService } from '@features/insured/services/member/member.service'
import { take } from 'rxjs/operators'
import { PeriodForm } from '@features/insured/models/period/period.model'
import { PolicyForm } from '@features/insured/models/policy/policy.model'
import { IfCategoryDirective } from '@features/certificate/directives/if-category.directive'
import { CERTIFICATE_DETAILS_VISIBILITY } from '@features/certificate/components/certificate-details/certificate-details.visibility'
import { CertificateCategory } from '@shared/models/certificate-category.model'

@Component({
  selector: 'albert-certificate-details',
  imports: [
    ReactiveFormsModule,
    AlbertInputComponent,
    AlbertInputWrapper,
    AlbertCheckboxComponent,
    AlbertDatepickerComponent,
    AlbertSelectComponent,
    CertificateFreetextSectionComponent,
    InputText,
    IftaLabel,
    AutoComplete,
    TableModule,
    FormsModule,
    IfCategoryDirective
  ],
  templateUrl: './certificate-details.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateDetailsComponent extends CertificateBaseComponent {
  public readonly form = input.required<FormGroup<CertificateDetailsForm>>()
  public readonly metaDataForm = input.required<FormGroup<CertificateMetaDataForm>>()
  public readonly periodForm = input.required<FormGroup<PeriodForm>>()
  public readonly policyForm = input.required<FormGroup<PolicyForm>>()
  public readonly subjectToCertificationOptions = SUBJECT_TO_CERTIFICATION_OPTIONS
  public readonly authoritiesSpecialFormatOptions = AUTHORITIES_SPECIAL_FORMAT_OPTIONS
  public readonly certificateAuthorOptions = CERTIFICATE_AUTHOR_OPTIONS
  public readonly periodOptions = input<{ label: string; value: string }[]>()
  public readonly policyOptions = input<{ label: string; value: string }[]>()
  public readonly visibility = CERTIFICATE_DETAILS_VISIBILITY
  public readonly CertificateCategory = CertificateCategory
  public readonly freetextSections: FreetextSection<'subjectToCertificationFreetext'>[] = [
    freetextSection('subjectToCertificationFreetext', 'Subject to Certification Freetext', 'subject-to-certification-freetext')
  ]
  public readonly freetextSectionsByKey = setupFreetextSections(this.form, this.freetextSections)
  protected readonly parentCompanies = signal<ParentCompany[]>([])
  protected readonly selectedInsured = signal<ParentCompany | null>(null)
  private readonly metaDataFormValues = signal<Partial<{ [K in keyof CertificateMetaDataForm]: CertificateMetaDataForm[K]['value'] }>>({})
  private readonly memberService = inject(MemberService)
  private readonly formValues = signal<Partial<{ [K in keyof CertificateDetailsForm]: CertificateDetailsForm[K]['value'] }>>({})
  public readonly leftTableData = computed<MetaDataRow[]>(() => {
    const values = this.formValues()
    return [
      { label: 'Certificate Number', value: values.certificateIdValue?.certificateNumber },
      { label: 'Imported Doc', value: (values.importedDoc === true) ? 'Yes' : 'No' }
    ]
  })
  public readonly rightTableData = computed<MetaDataRow[]>(() => {
    const values = this.formValues()
    return [
      { label: 'Certificate Version', value: values.certificateIdValue?.certificateVersion },
      { label: 'Draft Version Number', value: values.certificateIdValue?.certificateDraftVersion }
    ]
  })
  public readonly statusLabel = computed<string | null>(() => {
    return this.mapEnumLabel(CERTIFICATE_STATUS, this.formValues().status)
  })
  public readonly documentStatusLabel = computed<string | null>(() => {
    return this.mapEnumLabel(DOCUMENT_STATUS, this.formValues().documentStatus)
  })

  constructor() {
    super()
    effect(() => {
      const certificateDetailsForm = this.form()
      const certificateDetailsFormSub = certificateDetailsForm.valueChanges.pipe(startWith(certificateDetailsForm.value)).subscribe(val => {
        this.formValues.set(val)
      })
      return () => certificateDetailsFormSub.unsubscribe()
    })
    effect(() => {
      const certificateMetaDataForm = this.metaDataForm()
      const sub = certificateMetaDataForm.valueChanges.pipe(startWith(certificateMetaDataForm.value)).subscribe(val => {
        this.metaDataFormValues.set(val)

        const member = val.member
        const current = this.selectedInsured()

        if (member?.uuid === null || member?.uuid === undefined) {
          if (current !== null) {
            this.selectedInsured.set(null)
          }
          return
        }

        if (current?.uuid !== member.uuid || current.name !== (member.name ?? '')) {
          this.selectedInsured.set({
            uuid: member.uuid,
            name: member.name ?? ''
          })
        }
      })
      return () => sub.unsubscribe()
    })

    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      syncFormCategoryState(this.form(), category, this.visibility)
    })
  }

  protected onInsuredSearch(event: AutoCompleteCompleteEvent): void {
    const category = this.categoryService.activeCertificateCategory()
    if (category === null) return
    this.memberService.getParentCompanies(category, event.query).pipe(take(1)).subscribe(companies => {
      this.parentCompanies.set(companies)
    })
  }

  protected onInsuredSelect(event: AutoCompleteSelectEvent): void {
    const company = event.value as ParentCompany
    this.selectedInsured.set(company)
    const control = this.metaDataForm().controls.member
    control.setValue({
      uuid: company.uuid,
      name: company.name,
      coverageType: control.value?.coverageType ?? null
    })
    control.markAsDirty()
    control.markAsTouched()
  }

  protected onInsuredClear(): void {
    this.selectedInsured.set(null)
    const control = this.metaDataForm().controls.member
    control.setValue(null)
    control.markAsDirty()
    control.markAsTouched()
  }

  private mapEnumLabel(enumObject: Record<string, string>, value: string | null | undefined): string | null {
    if (!hasActualValue(value)) return null
    return Object.entries(enumObject).find(([key, label]) => key === value || label === value)?.[1] ?? value
  }
}
